import type { MultiTax, Tax, TaxId, TaxInfo } from '@hero/schema';
import { CONFIG } from '../config';

/**
 * Look up a {@link TaxInfo} (id + percentage) by id from the shared
 * price config. The same registry the backend reads from
 * (`APP_CONFIG.price.taxes` is `{...CONFIG.price.taxes}`), so anywhere
 * that needs "what is the rate for `saudi_vat`?" should funnel here.
 */
export function getTaxInfo(taxId: TaxId): TaxInfo {
  return CONFIG.price.taxes[taxId];
}

/** Convenience: the tax rate as a decimal (e.g. `0.15` for SA VAT). */
export function getTaxRate(taxId: TaxId): number {
  return getTaxInfo(taxId).percentage;
}

/**
 * Build a fresh `Tax` entry with a zeroed `amount` (the actual amount
 * gets computed by {@link computeTaxTotals} once the subtotal is
 * known). Mirrors `AmountService.addTax`.
 */
export function makeTax(taxId: TaxId, taxOnCustomSubtotal?: number): Tax {
  const info = getTaxInfo(taxId);
  const tax: Tax = {
    id: info.id,
    percentage: info.percentage,
    amount: 0,
  };
  if (taxOnCustomSubtotal != null) {
    tax.taxOnCustomSubtotal = taxOnCustomSubtotal;
  }
  return tax;
}

/**
 * For each entry in `applied`, compute `amount = base × percentage`,
 * where `base` is the per-tax `taxOnCustomSubtotal` override or the
 * shared `subtotal`. Returns a new {@link MultiTax} with the per-entry
 * amounts populated and the rollup `total`.
 *
 * Pure mirror of `AmountService.calcTax` — the `||` (rather than `??`)
 * fallback is intentional and matches the source: a `taxOnCustomSubtotal`
 * of `0` falls through to the shared subtotal, same as the original.
 */
export function computeTaxTotals(applied: ReadonlyArray<Tax>, subtotal: number): MultiTax {
  let total = 0;
  const next: Tax[] = applied.map(tax => {
    const base = tax.taxOnCustomSubtotal || subtotal;
    const amount = base * tax.percentage;
    total += amount;
    return { ...tax, amount };
  });
  return { applied: next, total };
}

/**
 * Split a tax-inclusive total into its base subtotal + tax amount.
 * Same formula `AmountService` uses to back-derive
 * `itemsOriginalSubtotal` when the caller provides only `total`.
 *
 *   subtotal = total ÷ (1 + percentage)
 *   tax      = subtotal × percentage
 */
export function splitInclusiveTax(
  total: number,
  percentage: number,
): { subtotal: number; tax: number } {
  const subtotal = total / (1 + percentage);
  const tax = subtotal * percentage;
  return { subtotal, tax };
}

/**
 * Pick the "main" (canonical VAT) tax out of a multi-tax stack.
 * Currently always the Saudi VAT entry — mirrors `AmountService.getMainTax`.
 *
 * Returns `undefined` when no main tax is applied, so callers should
 * guard before reading `.percentage`.
 */
export function getMainTax(applied: ReadonlyArray<Tax> | undefined): Tax | undefined {
  return applied?.find(tax => tax.id === 'saudi_vat');
}
